import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Icon } from "semantic-ui-react";
import { useLoginCheck } from "../../hooks/useLoginCheck";
import "../../styles/scss/customerList.scss";

function CustomerListPage() {
  useLoginCheck();
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [beginnerEnrollments, setBeginnerEnrollments] = useState([]);
  const [filteredBeginnerEnrollments, setFilteredBeginnerEnrollments] =
    useState([]);
  const [customerDetail, setCustomerDetail] = useState({});
  const [searchKeyword, setSearchKeyword] = useState("");
  const [activeTab, setActiveTab] = useState("customers");
  const [admissionFee, setAdmissionFee] = useState("");
  const [annualFee, setAnnualFee] = useState("");
  const [firstMonthFee, setFirstMonthFee] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const searchInputRef = useRef(null);

  const formatYearMonth = (value) => {
    if (!value) return "—";
    const match = String(value).match(/^(\d{4})-(\d{2})/);
    if (!match) return value;
    return `${match[1]}年${match[2]}月`;
  };

  const isDrawerOpen = !!customerDetail.id;
  const isDetailLoaded = !!customerDetail.first_name;

  const handleDetail = async (customerId) => {
    setCustomerDetail({ id: 99999999 });
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_customer_details/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customer_id: customerId }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setCustomerDetail(data);
    } catch (error) {
      console.error("詳細の取得に失敗しました", error);
    }
  };

  const closeDrawer = () => setCustomerDetail({});

  const fetchCustomersData = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_customers/`);
      if (!response.ok) {
        throw new Error("ネットワークレスポンスが異常です。");
      }
      const data = await response.json();
      setCustomers(data);
    } catch (error) {
      console.error("データの取得中にエラーが発生しました:", error);
    }
  }, []);

  const fetchBeginnerEnrollmentsData = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_beginner_enrollments/`);
      if (!response.ok) {
        throw new Error("ネットワークレスポンスが異常です。");
      }
      const data = await response.json();
      setBeginnerEnrollments(data);
    } catch (error) {
      console.error("未経験者専用クラス一覧の取得に失敗しました:", error);
    }
  }, []);

  const refreshAllData = useCallback(async () => {
    await Promise.all([fetchCustomersData(), fetchBeginnerEnrollmentsData()]);
  }, [fetchBeginnerEnrollmentsData, fetchCustomersData]);

  useEffect(() => {
    refreshAllData();
  }, [refreshAllData]);

  // filter + sort: 入会処理がまだの方 (subscription_id 無し) を上に
  useEffect(() => {
    const keyword = searchKeyword.toLowerCase().trim();
    const base = !keyword
      ? customers
      : customers.filter(
          (customer) =>
            customer.id.toString().includes(keyword) ||
            customer.last_name.toLowerCase().includes(keyword) ||
            customer.first_name.toLowerCase().includes(keyword) ||
            `${customer.last_name}${customer.first_name}`
              .toLowerCase()
              .includes(keyword) ||
            `${customer.last_name} ${customer.first_name}`
              .toLowerCase()
              .includes(keyword) ||
            customer.last_name_yomi.toLowerCase().includes(keyword) ||
            customer.first_name_yomi.toLowerCase().includes(keyword) ||
            `${customer.last_name_yomi}${customer.first_name_yomi}`
              .toLowerCase()
              .includes(keyword) ||
            `${customer.last_name_yomi} ${customer.first_name_yomi}`
              .toLowerCase()
              .includes(keyword) ||
            customer.parent_last_name.toLowerCase().includes(keyword) ||
            customer.parent_first_name.toLowerCase().includes(keyword) ||
            customer.studio.toLowerCase().includes(keyword) ||
            customer.phone.includes(keyword) ||
            customer.mail.toLowerCase().includes(keyword) ||
            customer.start_month.includes(keyword),
        );

    // 通常会員タブの表示ルール:
    //   - 通常会員 (subscription_id あり) → 常に表示
    //   - 未経験者専用クラス会員 (subscription_id なし & 未経験者契約あり
    //     & 正式入会希望なし) → 除外 (未経験者専用クラスタブで管理)
    //     ここは「受講中か」ではなく「契約履歴があるか」で判定する。受講中かどうかで
    //     切ると、期間満了と同時に未入会のまま通常会員タブへ落ちてきて、
    //     入会も取消もできない行として永久に居座るため。
    //     満了後に正式入会を申し込めば is_full_enrollment_pending で戻ってくる。
    //   - 正式入会希望者 (is_full_enrollment_pending) → 通常会員タブに表示
    //     してスタッフが入会処理 (admission_view) を実行できるようにする
    //   - 通常の未入会者 (初心者契約なし) → 表示
    const visible = base.filter((customer) => {
      if (customer.subscription_id) return true;
      if (
        customer.has_beginner_enrollment &&
        !customer.is_full_enrollment_pending
      ) {
        return false;
      }
      return true;
    });

    // pending(未入会) を先頭に、内部順序は維持
    const sorted = [...visible].sort((a, b) => {
      const ap = a.subscription_id ? 1 : 0;
      const bp = b.subscription_id ? 1 : 0;
      return ap - bp;
    });
    setFilteredCustomers(sorted);
  }, [searchKeyword, customers]);

  useEffect(() => {
    const keyword = searchKeyword.toLowerCase().trim();
    const base = !keyword
      ? beginnerEnrollments
      : beginnerEnrollments.filter(
          (enrollment) =>
            `${enrollment.customer_name || ""}`
              .toLowerCase()
              .includes(keyword) ||
            `${enrollment.lesson_name || ""}`.toLowerCase().includes(keyword) ||
            `${enrollment.studio_name || ""}`.toLowerCase().includes(keyword) ||
            `${enrollment.payment_type_label || ""}`
              .toLowerCase()
              .includes(keyword) ||
            `${enrollment.payment_method_label || ""}`
              .toLowerCase()
              .includes(keyword) ||
            `${enrollment.status_label || ""}`
              .toLowerCase()
              .includes(keyword) ||
            `${enrollment.stripe_status_label || ""}`
              .toLowerCase()
              .includes(keyword),
        );
    setFilteredBeginnerEnrollments(base);
  }, [searchKeyword, beginnerEnrollments]);

  // 未経験者専用クラスタブの行を customer 単位にまとめる
  //   - 表示は customer 1 行 (代表 = enrollments[0] = 最新)
  //   - 同 customer 内で値が分岐しうる cell (店舗・開始日・終了日) のみ
  //     unique 値が 2 以上なら "他 +N" を控えめに併記
  const groupedBeginnerCustomers = useMemo(() => {
    const groupMap = new Map();
    filteredBeginnerEnrollments.forEach((enrollment) => {
      const existing = groupMap.get(enrollment.customer_id);
      if (existing) {
        existing.enrollments.push(enrollment);
        return;
      }
      groupMap.set(enrollment.customer_id, {
        customer_id: enrollment.customer_id,
        customer_name: enrollment.customer_name,
        customer_first_name_yomi: enrollment.customer_first_name_yomi,
        customer_last_name_yomi: enrollment.customer_last_name_yomi,
        parent_first_name: enrollment.parent_first_name,
        parent_last_name: enrollment.parent_last_name,
        customer_phone: enrollment.customer_phone,
        customer_mail: enrollment.customer_mail,
        customer_subscription_id: enrollment.customer_subscription_id,
        customer_plan: enrollment.customer_plan,
        enrollments: [enrollment],
      });
    });
    const countOtherUnique = (enrollments, toKey) => {
      const values = enrollments
        .map(toKey)
        .filter((v) => v !== null && v !== undefined && v !== "");
      return Math.max(0, new Set(values).size - 1);
    };
    // 同 customer 内に複数契約があるので、代表ステータスを優先度で決める。
    // 受講中 > 開始前 > 終了 > 途中解約 (1本でも生きていれば「受講中」)
    const STATUS_PRIORITY = ["active", "scheduled", "completed", "cancelled"];
    const STATUS_LABEL = {
      active: "受講中",
      scheduled: "開始前",
      completed: "終了",
      cancelled: "途中解約",
    };
    const resolveStatus = (enrollments) => {
      const keys = new Set(enrollments.map((e) => e.status));
      return STATUS_PRIORITY.find((k) => keys.has(k)) || null;
    };
    return Array.from(groupMap.values()).map((group) => ({
      ...group,
      status: resolveStatus(group.enrollments),
      status_label: STATUS_LABEL[resolveStatus(group.enrollments)] || "—",
      // 全契約が終了/解約済み & 未入会 & プランが未経験者専用クラスのまま
      // = 正式入会の申込をしないままフェードアウトしかけている人。
      // 通常会員タブには出さない代わりに、ここで要対応として目立たせる。
      needs_full_enrollment:
        !group.customer_subscription_id &&
        group.customer_plan === "未経験者専用クラス" &&
        !group.enrollments.some(
          (e) => e.status === "active" || e.status === "scheduled",
        ),
      studio_extra_count: countOtherUnique(
        group.enrollments,
        (e) => e.studio_name,
      ),
      start_date_extra_count: countOtherUnique(
        group.enrollments,
        (e) => e.start_date,
      ),
      end_date_extra_count: countOtherUnique(
        group.enrollments,
        (e) => e.end_date,
      ),
      payment_extra_count: countOtherUnique(
        group.enrollments,
        (e) => `${e.payment_type || ""}|${e.payment_method || ""}`,
      ),
    }));
  }, [filteredBeginnerEnrollments]);

  const pendingCount = filteredCustomers.filter(
    (c) => !c.subscription_id,
  ).length;
  const firstActiveId = (() => {
    const idx = filteredCustomers.findIndex((c) => c.subscription_id);
    return idx > 0 ? filteredCustomers[idx].id : null;
  })();
  const beginnerActiveCount = groupedBeginnerCustomers.filter((group) =>
    group.enrollments.some(
      (enrollment) =>
        enrollment.status === "active" || enrollment.status === "scheduled",
    ),
  ).length;
  const activeListCount =
    activeTab === "customers"
      ? filteredCustomers.length
      : groupedBeginnerCustomers.length;

  // ESC で drawer close
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") closeDrawer();
    };
    if (isDrawerOpen) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isDrawerOpen]);

  const handleBeginnerContinueMail = async (enrollmentId) => {
    const confirmed = window.confirm(
      "通常クラスへのご案内メールを送信しますか？",
    );
    if (!confirmed) {
      return;
    }

    setIsLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(
        `${apiUrl}/api/send_beginner_continue_mail/`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ enrollment_id: enrollmentId }),
        },
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "案内メールの送信に失敗しました");
      }
      alert(data.message || "案内メールを送信しました。");
    } catch (error) {
      console.error("案内メールの送信に失敗しました", error);
      alert(error.message || "案内メールの送信に失敗しました");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdmission = async () => {
    setIsLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/admission/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customer_id: customerDetail.id,
          admission_fee: admissionFee,
          annual_fee: annualFee,
          first_month_fee: firstMonthFee,
        }),
      });
      if (!response.ok) {
        const data = await response.json();
        alert(data.message);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      await response.json();
      await refreshAllData();
      alert("入会処理が完了しました。");
    } catch (error) {
      console.error("入会処理に失敗しました", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = async () => {
    setIsLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/cancel_admission/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customer_id: customerDetail.id }),
      });
      if (!response.ok) {
        const data = await response.json();
        alert(data.message);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      await response.json();
      await refreshAllData();
      alert("取消処理が完了しました。");
    } catch (error) {
      console.error("取消処理に失敗しました", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/delete_customer/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customer_id: customerDetail.id }),
      });
      if (!response.ok) {
        const data = await response.json();
        alert(data.message);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      await response.json();
      await refreshAllData();
      alert("退会処理が完了しました。");
    } catch (error) {
      console.error("退会処理に失敗しました", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setAdmissionFee(customerDetail.admission_fee || "");
    setAnnualFee(customerDetail.annual_fee || "");
    setFirstMonthFee(customerDetail.first_month_fee || "");
  }, [customerDetail]);

  return (
    <div className="customer-list-page">
      {isLoading && (
        <div className="fullscreen-loader">
          <div className="spinner" />
        </div>
      )}

      {/* header */}
      <div className="page-header">
        <div className="header-main">
          <div className="title-row">
            <h1>生徒一覧</h1>
            <span className="count-pill">
              <span className="num">{activeListCount}</span>名
              {activeTab === "customers" && pendingCount > 0 && (
                <span className="pending">未入会 {pendingCount}</span>
              )}
              {activeTab === "beginner" && beginnerActiveCount > 0 && (
                <span className="pending">受講中 {beginnerActiveCount}</span>
              )}
            </span>
          </div>
          <div className="tab-row">
            <button
              type="button"
              className={`tab-pill ${activeTab === "customers" ? "on" : ""}`}
              onClick={() => setActiveTab("customers")}
            >
              通常会員
            </button>
            <button
              type="button"
              className={`tab-pill ${activeTab === "beginner" ? "on" : ""}`}
              onClick={() => setActiveTab("beginner")}
            >
              未経験者専用クラス
            </button>
          </div>
        </div>
        <div className="search-bar">
          <Icon name="search" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="名前・店舗・メール・電話番号・レッスンで検索"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
          />
        </div>
      </div>

      {/* list */}
      <div className="panel">
        <div className="panel-body">
          {activeTab === "customers" ? (
            <div className="customer-grid-v2">
              <div className="grid-row grid-head">
                <div className="cell">ID</div>
                <div className="cell">名前</div>
                <div className="cell">フリガナ</div>
                <div className="cell">保護者</div>
                <div className="cell">入会店舗</div>
                <div className="cell">電話番号</div>
                <div className="cell">メール</div>
                <div className="cell">申込日</div>
                <div className="cell">入会月</div>
                <div className="cell center">詳細</div>
              </div>
              {filteredCustomers.length === 0 ? (
                <div className="empty-state">該当する生徒がいません</div>
              ) : (
                filteredCustomers.map((customer) => {
                  const pending = !customer.subscription_id;
                  const isFirstActive = customer.id === firstActiveId;
                  return (
                    <div
                      key={customer.id}
                      className={`grid-row ${pending ? "is-pending" : ""} ${
                        isFirstActive ? "first-active" : ""
                      } ${customerDetail.id === customer.id ? "is-active" : ""}`}
                      onClick={() => handleDetail(customer.id)}
                    >
                      <div className="cell id">#{customer.id}</div>
                      <div className="cell name">
                        <div className="name-stack">
                          {customer.is_full_enrollment_pending && (
                            <span className="mini-badge beginner-pending">
                              未経験者経由・正式入会希望
                            </span>
                          )}
                          {customer.has_beginner_enrollment &&
                            !customer.is_full_enrollment_pending && (
                              <span className="mini-badge beginner">
                                {customer.has_active_beginner_enrollment
                                  ? "未経験者クラス受講中"
                                  : "未経験者クラス履歴"}
                              </span>
                            )}
                          <span className="name-text">
                            {`${customer.last_name} ${customer.first_name}`}
                          </span>
                        </div>
                      </div>
                      <div className="cell kana">
                        {`${customer.last_name_yomi} ${customer.first_name_yomi}`}
                      </div>
                      <div className="cell parent">
                        {`${customer.parent_last_name} ${customer.parent_first_name}`.trim() ||
                          "—"}
                      </div>
                      <div className="cell studio">
                        <span className="studio-chip">
                          {customer.studio || "—"}
                        </span>
                        {pending && customer.plan_change_denied && (
                          <span style={{ marginLeft: 6, opacity: 0.7 }}>
                            {customer.plan_change_denied}
                          </span>
                        )}
                      </div>
                      <div className="cell phone">{customer.phone || "—"}</div>
                      <div className="cell mail">{customer.mail || "—"}</div>
                      <div className="cell date">
                        {customer.return_date
                          ? customer.return_date
                          : customer.created_at}
                      </div>
                      <div className="cell month">
                        {customer.start_month}
                        {customer.return_date ? " (再入会)" : ""}
                      </div>
                      <div className="cell center">
                        <button
                          className="detail-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDetail(customer.id);
                          }}
                        >
                          詳細
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          ) : (
            <div className="customer-grid-v2 beginner-grid">
              <div className="grid-row grid-head">
                <div className="cell">ID</div>
                <div className="cell">会員名</div>
                <div className="cell">フリガナ</div>
                <div className="cell">保護者</div>
                <div className="cell">店舗</div>
                <div className="cell">電話番号</div>
                <div className="cell">メール</div>
                <div className="cell">開始</div>
                <div className="cell">終了</div>
                <div className="cell">状態</div>
                <div className="cell">支払い</div>
                <div className="cell center">詳細</div>
              </div>
              {groupedBeginnerCustomers.length === 0 ? (
                <div className="empty-state">
                  該当する未経験者専用クラスがありません
                </div>
              ) : (
                groupedBeginnerCustomers.map((group) => {
                  const primary = group.enrollments[0];
                  return (
                    <div
                      key={group.customer_id}
                      className={`grid-row ${
                        customerDetail.id === group.customer_id
                          ? "is-active"
                          : ""
                      }`}
                      onClick={() => handleDetail(group.customer_id)}
                    >
                      <div className="cell id">#{group.customer_id}</div>
                      <div className="cell name">
                        <div className="name-stack">
                          {group.customer_subscription_id ? (
                            <span className="mini-badge regular">
                              通常会員兼任
                            </span>
                          ) : group.needs_full_enrollment ? (
                            <span className="mini-badge beginner-pending">
                              正式入会 未申込
                            </span>
                          ) : null}
                          <span className="name-text">
                            {group.customer_name}
                          </span>
                        </div>
                      </div>
                      <div className="cell kana">
                        {`${group.customer_last_name_yomi || ""} ${group.customer_first_name_yomi || ""}`.trim() ||
                          "—"}
                      </div>
                      <div className="cell parent">
                        {`${group.parent_last_name || ""} ${group.parent_first_name || ""}`.trim() ||
                          "—"}
                      </div>
                      <div className="cell studio">
                        <span className="studio-chip">
                          {primary.studio_name || "—"}
                        </span>
                        {group.studio_extra_count > 0 && (
                          <span className="extra-text">
                            他 +{group.studio_extra_count}
                          </span>
                        )}
                      </div>
                      <div className="cell phone">
                        {group.customer_phone || "—"}
                      </div>
                      <div className="cell mail">
                        {group.customer_mail || "—"}
                      </div>
                      <div className="cell date">
                        {formatYearMonth(primary.start_date)}
                        {group.start_date_extra_count > 0 && (
                          <span className="extra-text">
                            他 +{group.start_date_extra_count}
                          </span>
                        )}
                      </div>
                      <div className="cell date">
                        {formatYearMonth(primary.end_date)}
                        {group.end_date_extra_count > 0 && (
                          <span className="extra-text">
                            他 +{group.end_date_extra_count}
                          </span>
                        )}
                      </div>
                      <div className="cell status">
                        <span
                          className={`status-chip ${group.status || "unknown"}`}
                        >
                          {group.status_label}
                        </span>
                      </div>
                      <div className="cell payment">
                        <div>
                          {primary.payment_type_label}
                          {group.payment_extra_count > 0 && (
                            <span className="extra-text">
                              他 +{group.payment_extra_count}
                            </span>
                          )}
                        </div>
                        <div className="subtext">
                          {primary.payment_method_label}
                        </div>
                      </div>
                      <div className="cell center">
                        <button
                          className="detail-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDetail(group.customer_id);
                          }}
                        >
                          詳細
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>

      {/* drawer overlay */}
      <div
        className={`drawer-overlay ${isDrawerOpen ? "open" : ""}`}
        onClick={closeDrawer}
      />

      {/* drawer */}
      <aside className={`detail-drawer ${isDrawerOpen ? "open" : ""}`}>
        <button
          className="drawer-close"
          onClick={closeDrawer}
          aria-label="close"
        >
          <Icon name="close" />
        </button>

        {!isDetailLoaded ? (
          <div className="drawer-loading">
            <div className="spinner" style={{ margin: "0 auto 1rem" }} />
            読み込み中...
          </div>
        ) : (
          <>
            <div className="drawer-hero">
              <p className="hero-eyebrow">Member Profile</p>
              <div className="avatar">
                {customerDetail.icon_image ? (
                  <img src={customerDetail.icon_image} alt="" />
                ) : (
                  <Icon name="user" />
                )}
              </div>
              <h2 className="name">
                {`${customerDetail.last_name} ${customerDetail.first_name}`}
                {customerDetail.age ? (
                  <span className="age">/ {customerDetail.age}</span>
                ) : null}
              </h2>
              <p className="name-yomi">
                {`${customerDetail.last_name_yomi} ${customerDetail.first_name_yomi}`}
              </p>
              <div className="hero-meta">
                <span className="meta-chip">
                  ID <span className="num">#{customerDetail.id}</span>
                </span>
                {customerDetail.payment_method && (
                  <span className="meta-chip">
                    {customerDetail.payment_method}
                  </span>
                )}
                <span className="meta-chip">
                  {customerDetail.subscription_id ? "入会済" : "未入会"}
                </span>
                {customerDetail.is_resting && (
                  <span className="meta-chip">休会中</span>
                )}
              </div>
            </div>

            <div className="drawer-section">
              <p className="section-title">
                <span className="ja">基本情報</span>
                <span className="ja-sub">Basic</span>
              </p>
              <dl className="info-grid">
                <dt>Mail</dt>
                <dd>{customerDetail.mail || "—"}</dd>
                <dt>Tel</dt>
                <dd className="num">{customerDetail.phone || "—"}</dd>
                <dt>年齢</dt>
                <dd className="num">
                  {customerDetail.age ? `${customerDetail.age} 歳` : "—"}
                </dd>
                <dt>生年月日</dt>
                <dd className="num">{customerDetail.birthday || "—"}</dd>
              </dl>
            </div>

            <div className="drawer-section">
              <p className="section-title">
                <span className="ja">プラン</span>
                <span className="ja-sub">Plan</span>
              </p>
              {!customerDetail.now_plan && !customerDetail.new_plan && (
                <p className="muted">登録されているプランはありません</p>
              )}
              {customerDetail.now_plan && (
                <div className="plan-block">
                  <p className="plan-label">Current Plan</p>
                  <p className="plan-name">{customerDetail.now_plan.name}</p>
                  {customerDetail.now_plan.start_month && (
                    <div className="plan-meta">
                      <span className="k">Start</span>
                      <span className="v num">
                        {customerDetail.now_plan.start_month}
                      </span>
                    </div>
                  )}
                  {customerDetail.now_plan.lessons &&
                    customerDetail.now_plan.lessons.length > 0 && (
                      <div className="plan-lessons">
                        <p className="lessons-label">Lessons</p>
                        {customerDetail.now_plan.lessons.map((lesson, i) => (
                          <div className="lesson" key={i}>
                            {lesson}
                          </div>
                        ))}
                      </div>
                    )}
                </div>
              )}
              {customerDetail.new_plan && (
                <div className="plan-block">
                  <p className="plan-label">Upcoming Plan</p>
                  <p className="plan-name">{customerDetail.new_plan.name}</p>
                  {customerDetail.new_plan.start_month && (
                    <div className="plan-meta">
                      <span className="k">Start</span>
                      <span className="v num">
                        {customerDetail.new_plan.start_month}
                      </span>
                    </div>
                  )}
                  {customerDetail.new_plan.lessons &&
                    customerDetail.new_plan.lessons.length > 0 && (
                      <div className="plan-lessons">
                        <p className="lessons-label">Lessons</p>
                        {customerDetail.new_plan.lessons.map((lesson, i) => (
                          <div className="lesson" key={i}>
                            {lesson}
                          </div>
                        ))}
                      </div>
                    )}
                </div>
              )}
            </div>

            <div className="drawer-section">
              <p className="section-title">
                <span className="ja">未経験者専用クラス</span>
                <span className="ja-sub">Beginner Enrollment</span>
              </p>
              {customerDetail.beginner_enrollments &&
              customerDetail.beginner_enrollments.length > 0 ? (
                <div className="beginner-list">
                  {customerDetail.beginner_enrollments.map((enrollment) => (
                    <div className="beginner-card" key={enrollment.id}>
                      <div className="beginner-card-head">
                        <div>
                          <p className="beginner-title">
                            {[
                              enrollment.instructor_name,
                              enrollment.studio_name,
                              enrollment.lesson_name,
                            ]
                              .filter(Boolean)
                              .join(" ")}
                          </p>
                          <p className="beginner-sub">
                            {formatYearMonth(enrollment.start_date)} 〜{" "}
                            {formatYearMonth(enrollment.end_date)}
                          </p>
                        </div>
                      </div>
                      <dl className="info-grid beginner-info">
                        <dt>支払い</dt>
                        <dd>
                          {enrollment.payment_type_label} /{" "}
                          {enrollment.payment_method_label}
                        </dd>
                        <dt>期間</dt>
                        <dd>
                          {formatYearMonth(enrollment.start_date)} 〜{" "}
                          {formatYearMonth(enrollment.end_date)}
                        </dd>
                      </dl>
                      {/* <div className="beginner-actions">
                        <button
                          className="detail-btn"
                          onClick={() =>
                            handleBeginnerContinueMail(enrollment.id)
                          }
                        >
                          案内メール送信
                        </button>
                      </div> */}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="muted">未経験者専用クラスの登録はありません</p>
              )}
            </div>

            <div className="drawer-section">
              <p className="section-title">
                <span className="ja">支払い履歴</span>
                <span className="ja-sub">
                  Invoices · {customerDetail.payment_method || "—"}
                </span>
              </p>
              {customerDetail.invoices && customerDetail.invoices.length > 0 ? (
                <div className="history-list">
                  {customerDetail.invoices.map((invoice, index) => (
                    <a
                      key={index}
                      href={invoice.hosted_invoice_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="history-item"
                    >
                      <span className="h-date">{invoice.created_at}</span>
                      <span className="h-amount">
                        {Number(invoice.amount).toLocaleString()}
                        <span className="yen">円</span>
                      </span>
                      {invoice.status === "paid" && (
                        <span className="badge paid">Paid</span>
                      )}
                      {invoice.status === "open" && (
                        <span className="badge open">Open</span>
                      )}
                    </a>
                  ))}
                </div>
              ) : (
                <p className="muted">支払い履歴はありません</p>
              )}
            </div>

            <div className="drawer-section">
              <p className="section-title">
                <span className="ja">受講履歴</span>
                <span className="ja-sub">Attendance</span>
              </p>
              {customerDetail.attendances &&
              customerDetail.attendances.length > 0 ? (
                <div className="history-list">
                  {customerDetail.attendances.map((attendance, index) => (
                    <div key={index} className="history-item">
                      <span className="h-date">{attendance.date}</span>
                      <span className="h-text">{attendance.lesson}</span>
                      {attendance.is_monthly ? (
                        <span className="badge monthly">Monthly</span>
                      ) : (
                        <span />
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="muted">受講履歴はありません</p>
              )}
            </div>

            <div className="drawer-section">
              <p className="section-title">
                <span className="ja">体験履歴</span>
                <span className="ja-sub">Trials</span>
              </p>
              {customerDetail.trials && customerDetail.trials.length > 0 ? (
                <div className="history-list">
                  {customerDetail.trials.map((trial, index) => (
                    <div key={index} className="history-item">
                      <span className="h-date">{trial.date}</span>
                      <span className="h-text">
                        {trial.studio} / {trial.name}
                      </span>
                      <span />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="muted">体験履歴はありません</p>
              )}
            </div>

            {customerDetail.subscription_id ? (
              <div className="drawer-actions">
                <button
                  className="danger"
                  onClick={handleDelete}
                  disabled={isLoading}
                >
                  退会処理
                </button>
              </div>
            ) : activeTab !== "beginner" ? (
              <>
                <div className="drawer-section">
                  <p className="section-title">
                    <span className="ja">入会処理</span>
                    <span className="ja-sub">Admission</span>
                  </p>
                  {customerDetail.referral_info?.applied && (
                    <div
                      style={{
                        margin: "0 0 14px",
                        padding: "12px 14px",
                        borderRadius: "10px",
                        background: "rgba(255, 45, 85, 0.10)",
                        border: "1px solid rgba(255, 45, 85, 0.35)",
                        lineHeight: 1.6,
                        color: "#fff",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 700,
                          color: "#e0457a",
                          marginBottom: 4,
                        }}
                      >
                        🎁 紹介キャンペーン適用者
                      </div>
                      <div style={{ fontSize: 13 }}>
                        紹介者:{" "}
                        <strong>
                          {customerDetail.referral_info.referrer_name ||
                            "(不明)"}
                        </strong>{" "}
                        さん
                        {customerDetail.referral_info.status === "rewarded"
                          ? "（特典付与済み）"
                          : ""}
                      </div>
                      <div
                        style={{ fontSize: 12, opacity: 0.85, marginTop: 4 }}
                      >
                        初月月謝に ¥
                        {(
                          customerDetail.referral_info.reward_amount || 2000
                        ).toLocaleString()}{" "}
                        OFF
                        を反映してください。※他のキャンペーンとの併用は不可。お得な方を適用してください。
                      </div>
                    </div>
                  )}
                  <div className="field">
                    <label>入会金 / Admission Fee</label>
                    <div className="input-wrap">
                      <span className="yen-prefix">¥</span>
                      <input
                        placeholder="0"
                        value={admissionFee}
                        onChange={(e) => setAdmissionFee(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="field">
                    <label>年会費 / Annual Fee</label>
                    <div className="input-wrap">
                      <span className="yen-prefix">¥</span>
                      <input
                        placeholder="0"
                        value={annualFee}
                        onChange={(e) => setAnnualFee(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="field">
                    <label>初月月謝 / First Month</label>
                    <div className="input-wrap">
                      <span className="yen-prefix">¥</span>
                      <input
                        placeholder="0"
                        value={firstMonthFee}
                        onChange={(e) => setFirstMonthFee(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div className="drawer-actions">
                  <button
                    className="primary"
                    onClick={handleAdmission}
                    disabled={isLoading}
                  >
                    入会する
                  </button>
                  <button
                    className="ghost"
                    onClick={handleCancel}
                    disabled={isLoading}
                  >
                    取消
                  </button>
                </div>
              </>
            ) : null}
          </>
        )}
      </aside>
    </div>
  );
}

export default CustomerListPage;
